import { getUserPreferences, updateUserPreferences } from "../../api";
import React, { useRef, useState, useEffect } from "react";
import {
  User,
  Briefcase,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Twitter,
  Facebook,
  Linkedin,
  Instagram,
  Pencil,
  X,
  BriefcaseBusiness,
  Landmark,
  Clock,
  CalendarOff,
  Plus,
  Trash2,

} from "lucide-react";
import RoleDropdown from "../RoleDropdown";
import OfficeDropdown from "../OfficeDropdown";
import Cookies from "js-cookie";
import axios from "axios";
import { resolveAssignableOrganizations } from "../../../../../utils/officeCodeContext";
import { formatTo12Hour } from "../../workingHoursUtils";
import { usePrivilege } from "../../../../../guard/usePrivilege";

const DAYS_OF_WEEK = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];
const DAY_LABELS = {
  MONDAY: "Monday",
  TUESDAY: "Tuesday",
  WEDNESDAY: "Wednesday",
  THURSDAY: "Thursday",
  FRIDAY: "Friday",
  SATURDAY: "Saturday",
  SUNDAY: "Sunday",
};

const getShiftForDay = (workingHours, day) =>
  (workingHours || []).find((w) => w.dayOfWeek === day);

const setShiftForDay = (workingHours, day, field, value) => {
  const others = (workingHours || []).filter((w) => w.dayOfWeek !== day);
  const current = getShiftForDay(workingHours, day) || {
    dayOfWeek: day,
    startTime: "",
    endTime: "",
  };
  const updated = { ...current, [field]: value };
  if (!updated.startTime && !updated.endTime) {
    return others; // both cleared — treat the day as off, drop the entry
  }
  return [...others, updated];
};


const formatCategory = (category) => {
  if (!category) return "—";

  return category
    .replace(/_/g, " ")
    .split(" ")
    .map((word) =>
      word.toLowerCase() === "edi"
        ? "EDI"
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(" ");
};

const ViewUserProfile = ({ userData: initialUserData }) => {
  const [userData, setUserData] = useState(initialUserData);
  const [formData, setFormData] = useState(initialUserData || {});
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingHours, setIsEditingHours] = useState(false);
  const [newTimeOffDate, setNewTimeOffDate] = useState("");
  const [newTimeOffReason, setNewTimeOffReason] = useState("");
  const [savingTimeOff, setSavingTimeOff] = useState(false);
  const [timeOffError, setTimeOffError] = useState("");

  const [preferences, setPreferences] = useState(null);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [updatingPreferences, setUpdatingPreferences] = useState(false);
  const scrollContainerRef = useRef(null);

  const token = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCode");
  const currentUserData = JSON.parse(Cookies.get("userData") || "{}");
  const isOwnProfile = currentUserData.email === userData.email;
  // LGX-787: a non-admin viewing a team member's profile from the Manage Team grid must get a
  // read-only page -- no edit pencils, no Time Off add/remove, no Roles/Organizations "+".
  // Notification preferences are left alone below, they're already correctly isOwnProfile-gated.
  const { hasPrivilege } = usePrivilege();
  const canManageTeam = hasPrivilege("manageTeam:ADMIN");
  const displayedOrganizations = isOwnProfile
    ? resolveAssignableOrganizations(userData.organizations).sort((a, b) =>
        String(a.organizationName ?? a.officeCode).localeCompare(
          String(b.organizationName ?? b.officeCode)
        )
      )
    : userData.organizations ?? [];


  // 🔁 Fetch latest profile
  const refreshUserProfile = async () => {
    if (!initialUserData?.email) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/viewprofile`,
        {
          params: { userEmail: initialUserData.email },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUserData(res.data);
      setFormData(res.data);
      console.log("✅ Refreshed user profile:", res.data);

      // Reload preferences if viewing own profile
      if (isOwnProfile) {
        await loadUserPreferences();
      }

    } catch (err) {
      console.error("❌ Failed to refresh user profile:", err);
    } finally {
      setLoading(false);
    }
  };

  //Update user profile
  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/updateprofile`,
        formData,
        {
          params: { userEmail: userData.email },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
            await refreshUserProfile();
      setIsEditing(false);
    } catch (err) {
      console.error("❌ Failed to update profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimeOff = async () => {
    if (!newTimeOffDate) return;
    setSavingTimeOff(true);
    setTimeOffError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/timeoff`,
        { date: newTimeOffDate, reason: newTimeOffReason || null },
        {
          params: { userEmail: userData.email },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNewTimeOffDate("");
      setNewTimeOffReason("");
      await refreshUserProfile();
    } catch (err) {
      setTimeOffError(
        err?.response?.data?.content || "Failed to add time off."
      );
    } finally {
      setSavingTimeOff(false);
    }
  };

  const handleRemoveTimeOff = async (date) => {
    setSavingTimeOff(true);
    setTimeOffError("");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/auth/user/timeoff`,
        {
          params: { userEmail: userData.email, date },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await refreshUserProfile();
    } catch (err) {
      setTimeOffError(
        err?.response?.data?.content || "Failed to remove time off."
      );
    } finally {
      setSavingTimeOff(false);
    }
  };


  const loadUserPreferences = async () => {
    if (!userData?.email) return;
    try {
      setPreferencesLoading(true);
      const res = await getUserPreferences();
      setPreferences(res.data);
    } catch (err) {
      console.error("Failed to fetch preferences:", err);
    } finally {
      setPreferencesLoading(false);
    }
  };

  // 🔧 Update preference immediately
  const handleTogglePreference = async (pref) => {
    try {
      setUpdatingPreferences(true);
      await updateUserPreferences(pref.category, !pref.isEnabled);
      // Update local preferences state
      setPreferences((prevPreferences) =>
        prevPreferences.map((p) =>
          p.id === pref.id ? { ...p, isEnabled: !p.isEnabled } : p
        )
      );
          } catch (err) {
      console.error("❌ Failed to update preference:", err);
    } finally {
      setUpdatingPreferences(false);
    }
  };

  useEffect(() => {
    refreshUserProfile();
  }, [initialUserData?.email]);

  useEffect(() => {
    if (userData?.email && isOwnProfile) {
      loadUserPreferences();
    }
  }, [userData?.email, isOwnProfile]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-acs-dark-subtext">
        Loading profile...
      </div>
    );

  if (!userData)
    return (
      <div className="p-6 text-gray-500 dark:text-acs-dark-subtext">
        No profile data available.
      </div>
    );

  const fullName = `${userData.firstName || ""} ${userData.lastName || ""}`.trim();

  const renderAvatar = () => {
    if (userData.profilePicture)
      return (
        <img
          src={userData.profilePicture}
          alt="Profile"
          className="w-24 h-24 rounded-full object-cover shadow-sm"
        />
      );
    const initials = (userData.firstName?.[0] || "U") + (userData.lastName?.[0] || "");
    return (
      <div className="w-24 h-24 rounded-full bg-acs-accent/20 dark:bg-acs-accent/30 flex items-center justify-center text-2xl font-semibold text-acs-accent shadow-inner">
        {initials}
      </div>
    );
  };

  const infoIcons = {
    firstName: <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    lastName: <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    email: <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    job: <BriefcaseBusiness className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    industry: <Landmark className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    phoneNumber: <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    address: <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    country: <Globe className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    twitter: <Twitter className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    facebook: <Facebook className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    linkedin: <Linkedin className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
    instagram: <Instagram className="w-4 h-4 text-gray-400 dark:text-gray-500" />,
  };

  return (
    <div
      ref={scrollContainerRef}
      className="w-full h-full min-h-0 flex flex-col transition-opacity duration-500 bg-gray-50 dark:bg-acs-dark-bg"
    >
      {/* === Top Header === */}
      <div className="sticky top-0 z-10 bg-white dark:bg-acs-dark-surface shadow-md p-fluid-2 flex flex-col gap-fluid-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-fluid-3">
          <div className="flex items-center gap-fluid-3">
            {renderAvatar()}
            <div>
              <h2 className="text-fluid-xl font-semibold text-gray-900 dark:text-acs-dark-text">
                {fullName}
              </h2>
              {userData.companyName && 
              <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                {userData.companyName}
              </p>
              }
              <div className="mt-fluid-1 flex items-center gap-2">
                <span className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-2 py-[2px] text-xs rounded-md font-medium">
                  Active
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* === Two Column Layout === */}
      <div className="flex-1 overflow-y-auto bg-white dark:bg-acs-dark-bg w-full p-fluid-3 grid grid-cols-1 md:grid-cols-2 gap-fluid-3">
        {/* LEFT COLUMN */}
        <div className="space-y-fluid-3">
          {/* === Contact Information === */}
          <div
            className={`bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3 ${
              isEditing ? "ring-2 ring-indigo-300 dark:ring-acs-accent" : ""
            }`}
          >
            <div className="flex items-center justify-between mb-fluid-2">
              <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                Contact Information
              </h3>
              {canManageTeam && (
                <button
                  onClick={() => setIsEditing((prev) => !prev)}
                  className="text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-acs-accent transition"
                >
                  {isEditing ? <X className="w-5 h-5" /> : <Pencil className="w-5 h-5" />}
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-3 text-fluid-base text-gray-700 dark:text-acs-dark-text">
                {[
                  "firstName",
                  "lastName",
                  "job",
                  "industry",
                  "phoneNumber",
                  "address",
                  "country",
                  "twitter",
                  "facebook",
                  "linkedin",
                  "instagram",
                ].map((field) => (
                  <div key={field}>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-acs-dark-subtext mb-1 capitalize">
                      {infoIcons[field]}
                      {field}
                    </label>
                    <input
                      type="text"
                      value={formData[field] || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, [field]: e.target.value }))
                      }
                      placeholder={`Enter ${field}`}
                      className="w-full border border-gray-300 dark:border-acs-dark-border rounded-md p-2 text-sm bg-white dark:bg-[#2A2D35] text-gray-800 dark:text-acs-dark-text focus:ring-2 focus:ring-indigo-400 dark:focus:ring-acs-accent focus:outline-none"
                    />
                  </div>
                ))}

                {/* Non-editable email */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-acs-dark-subtext mb-1">
                    {infoIcons.email}
                    Email
                  </label>
                  <input
                    type="text"
                    value={userData.email}
                    disabled
                    className="w-full border border-gray-200 dark:border-acs-dark-border rounded-md p-2 text-sm bg-gray-100 dark:bg-[#1F2128] text-gray-500 dark:text-acs-dark-subtext"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-acs-dark-border hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveProfile}
                    className="px-4 py-2 text-sm rounded-md bg-indigo-600 dark:bg-acs-accent text-white hover:bg-indigo-700 dark:hover:bg-acs-accent/80"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-fluid-base text-gray-700 dark:text-acs-dark-text">
                {[
                  ["firstName", userData.firstName],
                  ["lastName", userData.lastName],
                  ["email", userData.email],
                  ["job", userData.job],
                  ["industry", userData.industry],
                  ["phoneNumber", userData.phoneNumber],
                  ["address", userData.address],
                  ["country", userData.country],
                  ["twitter", userData.twitter],
                  ["facebook", userData.facebook],
                  ["linkedin", userData.linkedin],
                  ["instagram", userData.instagram],
                ].map(([field, value]) => (
                  <div key={field} className="flex items-center gap-2">
                    {infoIcons[field]}
                    <p>
                      <span className="font-medium text-gray-600 dark:text-acs-dark-subtext capitalize">
                        {field}:
                      </span>{" "}
                      {value || "—"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* === Working Hours === */}
          <div
            className={`bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3 ${
              isEditingHours ? "ring-2 ring-indigo-300 dark:ring-acs-accent" : ""
            }`}
          >
            <div className="flex items-center justify-between mb-fluid-2">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-indigo-600 dark:text-acs-accent" />
                <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                  Working Hours
                </h3>
              </div>
              {canManageTeam && (
                <button
                  onClick={() => setIsEditingHours((prev) => !prev)}
                  className="text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-acs-accent transition"
                >
                  {isEditingHours ? <X className="w-5 h-5" /> : <Pencil className="w-5 h-5" />}
                </button>
              )}
            </div>

            {isEditingHours ? (
              <div className="text-fluid-base text-gray-700 dark:text-acs-dark-text">
                <div className="space-y-2">
                  {DAYS_OF_WEEK.map((day) => {
                    const shift = getShiftForDay(formData.workingHours, day);
                    return (
                      <div key={day} className="flex items-center gap-3">
                        <span className="w-24 shrink-0 text-xs font-medium text-gray-500 dark:text-acs-dark-subtext">
                          {DAY_LABELS[day]}
                        </span>
                        <input
                          type="time"
                          value={(shift?.startTime || "").slice(0, 5)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workingHours: setShiftForDay(
                                prev.workingHours,
                                day,
                                "startTime",
                                e.target.value
                              ),
                            }))
                          }
                          className="flex-1 min-w-0 border border-gray-300 dark:border-acs-dark-border rounded-md p-2 text-sm bg-white dark:bg-[#2A2D35] text-gray-800 dark:text-acs-dark-text focus:ring-2 focus:ring-indigo-400 dark:focus:ring-acs-accent focus:outline-none"
                        />
                        <span className="text-gray-400 dark:text-acs-dark-subtext">to</span>
                        <input
                          type="time"
                          value={(shift?.endTime || "").slice(0, 5)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              workingHours: setShiftForDay(
                                prev.workingHours,
                                day,
                                "endTime",
                                e.target.value
                              ),
                            }))
                          }
                          className="flex-1 min-w-0 border border-gray-300 dark:border-acs-dark-border rounded-md p-2 text-sm bg-white dark:bg-[#2A2D35] text-gray-800 dark:text-acs-dark-text focus:ring-2 focus:ring-indigo-400 dark:focus:ring-acs-accent focus:outline-none"
                        />
                        {shift && (
                          <button
                            type="button"
                            title="Clear (day off)"
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                workingHours: (prev.workingHours || []).filter(
                                  (w) => w.dayOfWeek !== day
                                ),
                              }))
                            }
                            className="shrink-0 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-end gap-3 pt-fluid-2">
                  <button
                    onClick={() => setIsEditingHours(false)}
                    className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-acs-dark-border hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await handleSaveProfile();
                      setIsEditingHours(false);
                    }}
                    className="px-4 py-2 text-sm rounded-md bg-indigo-600 dark:bg-acs-accent text-white hover:bg-indigo-700 dark:hover:bg-acs-accent/80"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : userData.workingHours?.length ? (
              <div className="space-y-1.5 text-fluid-base text-gray-700 dark:text-acs-dark-text">
                {DAYS_OF_WEEK.map((day) => {
                  const shift = getShiftForDay(userData.workingHours, day);
                  return (
                    <div key={day} className="flex items-center gap-2">
                      <span className="w-24 shrink-0 text-xs font-medium text-gray-500 dark:text-acs-dark-subtext">
                        {DAY_LABELS[day]}
                      </span>
                      {shift ? (
                        <span>
                          {formatTo12Hour(shift.startTime)} – {formatTo12Hour(shift.endTime)}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-acs-dark-subtext">Off</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                No working hours set
              </p>
            )}
          </div>

          {/* === Time Off === */}
          <div className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
            <div className="flex items-center gap-2 mb-fluid-2">
              <CalendarOff className="w-5 h-5 text-indigo-600 dark:text-acs-accent" />
              <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                Time Off
              </h3>
            </div>

            {timeOffError && (
              <p className="text-xs text-red-500 mb-fluid-2">{timeOffError}</p>
            )}

            <div className="space-y-2 mb-fluid-2">
              {userData.timeOff?.length ? (
                userData.timeOff.map((t) => (
                  <div
                    key={t.date}
                    className="flex items-center justify-between rounded-md border border-gray-200 dark:border-acs-dark-border bg-white dark:bg-[#22242B] px-3 py-2 text-sm"
                  >
                    <div>
                      <span className="font-medium text-gray-800 dark:text-acs-dark-text">
                        {t.date}
                      </span>
                      {t.reason && (
                        <span className="ml-2 text-gray-500 dark:text-acs-dark-subtext">
                          {t.reason}
                        </span>
                      )}
                    </div>
                    {canManageTeam && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTimeOff(t.date)}
                        disabled={savingTimeOff}
                        title="Remove"
                        className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                  No time off scheduled
                </p>
              )}
            </div>

            {canManageTeam && (
              <div className="flex items-end gap-2 pt-fluid-1 border-t border-gray-100 dark:border-acs-dark-border">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-acs-dark-subtext mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={newTimeOffDate}
                    onChange={(e) => setNewTimeOffDate(e.target.value)}
                    className="w-full border border-gray-300 dark:border-acs-dark-border rounded-md p-2 text-sm bg-white dark:bg-[#2A2D35] text-gray-800 dark:text-acs-dark-text focus:ring-2 focus:ring-indigo-400 dark:focus:ring-acs-accent focus:outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-acs-dark-subtext mb-1">
                    Reason (optional)
                  </label>
                  <input
                    type="text"
                    value={newTimeOffReason}
                    onChange={(e) => setNewTimeOffReason(e.target.value)}
                    placeholder="Vacation, sick day..."
                    className="w-full border border-gray-300 dark:border-acs-dark-border rounded-md p-2 text-sm bg-white dark:bg-[#2A2D35] text-gray-800 dark:text-acs-dark-text focus:ring-2 focus:ring-indigo-400 dark:focus:ring-acs-accent focus:outline-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddTimeOff}
                  disabled={savingTimeOff || !newTimeOffDate}
                  className="shrink-0 flex items-center gap-1 px-3 py-2 text-sm rounded-md bg-indigo-600 dark:bg-acs-accent text-white hover:bg-indigo-700 dark:hover:bg-acs-accent/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            )}
          </div>


          {/* === Roles === */}
          <div className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
            <div className="flex items-center justify-between mb-fluid-2">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-600 dark:text-acs-accent" />
                <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                  Roles
                </h3>
              </div>
              {canManageTeam && (
                <RoleDropdown
                  userEmail={userData.email}
                  userRoles={userData.roles}
                  onRoleChange={refreshUserProfile}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {userData.roles?.length ? (
                userData.roles.map((role) => (
                  <span
                    key={role.id}
                    className="bg-indigo-50 dark:bg-[#22368833] border border-indigo-200 dark:border-acs-dark-border text-indigo-700 dark:text-acs-accent px-3 py-1 rounded-md text-xs font-medium"
                  >
                    {role.name}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                  No roles assigned
                </p>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-fluid-3">
          {/* === Organizations === */}
          <div className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3">
            <div className="flex items-center justify-between mb-fluid-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600 dark:text-acs-accent" />
                <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                  Organizations
                </h3>
              </div>
              {canManageTeam && (
                <OfficeDropdown
                  userEmail={userData.email}
                  userId={userData.id}
                  userOrganizations={userData.organizations}
                  officeCode={officeCode}
                  onOfficeChange={refreshUserProfile}
                />
              )}
            </div>
            <div className="space-y-2 text-sm">
              {displayedOrganizations.length ? (
                displayedOrganizations.map((org) => (
                  <div
                    key={org.officeCode}
                    className="border border-gray-100 dark:border-acs-dark-border rounded-md p-2 bg-white dark:bg-[#22242B]"
                  >
                    <p className="font-medium text-gray-800 dark:text-acs-dark-text">
                      {org.organizationName || org.officeCode}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-acs-dark-subtext">
                      {org.officeCode}
                    </p>
                    {org.department && (
                      <p className="text-xs text-gray-500 dark:text-acs-dark-subtext">
                        Dept: {org.department}
                        </p>
                      )}

                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                  No organizations assigned
                </p>
              )}
            </div>
          </div>

          {/* === Notification Preferences === */}
          {isOwnProfile && Array.isArray(preferences) && preferences.length > 0 && (
            <div
              className="bg-gray-50 dark:bg-acs-dark-surface border border-gray-100 dark:border-acs-dark-border rounded-xl shadow-sm p-fluid-3"
            >
              <div className="flex items-center justify-between mb-fluid-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600 dark:text-acs-accent" />
                  <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                    Notification Preferences
                  </h3>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-fluid-2">
                Each toggle controls both the email and the in-app bell notification for that category.
              </p>
              <div className="space-y-2 text-sm">
                {preferences.map((pref, idx) => (
                  <div
                    key={pref.id || idx}
                    className="flex items-center justify-between border border-gray-200 dark:border-acs-dark-border rounded-md p-3 bg-white dark:bg-[#22242B]"
                  >
                    <p className="font-medium text-gray-900 dark:text-acs-dark-text">
                      {formatCategory(pref.category)}
                    </p>
                    <button
                      onClick={() => handleTogglePreference(pref)}
                      disabled={updatingPreferences}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50 ${
                        pref.isEnabled
                          ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/60"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pref.isEnabled ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewUserProfile;
